import { removeAttrs, setAttr } from '@common/html/util/dom';
import { parseClassString, parseStyles, parseStyleValue } from '@common/html/util/style';

/**
 * Syncs a set of attributes onto an element, removing stale attributes and updating existing ones.
 * The caller owns the lifecycle of the `applied` map and should reuse it per element.
 * The map stores previous values for non-class attributes and an array of class names for `class`.
 * @param element The element on which to sync the attributes.
 * @param next The next set of attributes to apply.
 * @param applied A map of attribute name -> previous value (or string[] for class).
 */
export function syncDomAttrs(element: HTMLElement, next: Dictionary | null | undefined, applied: Map<string, any>) {
  const attrs = next ?? {};

  // Removals: attributes tracked but no longer present
  for (const [name, prev] of applied.entries()) {
    if (!(name in attrs)) {
      if (name === 'class') {
        const prevClasses: string[] = Array.isArray(prev) ? prev : [];
        if (prevClasses.length > 0) {
          element.classList.remove(...prevClasses);
        }
      } else if (name === 'style') {
        const prevProps: string[] = Array.isArray(prev) ? prev : [];
        for (const prop of prevProps) {
          element.style.removeProperty(prop);
        }
      } else {
        removeAttrs(element, [name]);
      }
      applied.delete(name);
    }
  }

  // Apply / update present attributes
  for (const [name, value] of Object.entries(attrs)) {
    if (value === false || value === null || value === undefined) {
      if (applied.has(name)) {
        if (name === 'class') {
          const prevClasses: string[] = applied.get(name) ?? [];
          if (prevClasses.length > 0) {
            element.classList.remove(...prevClasses);
          }
        } else if (name === 'style') {
          const prevProps: string[] = applied.get(name) ?? [];
          for (const prop of prevProps) {
            element.style.removeProperty(prop);
          }
        } else {
          removeAttrs(element, [name]);
        }
        applied.delete(name);
      }
      continue;
    }

    if (name === 'class') {
      const prevClasses: string[] = applied.get('class') ?? [];
      const nextClasses = parseClassString(String(value));
      // Remove classes we previously added but are no longer present
      for (const cls of prevClasses) {
        if (!nextClasses.includes(cls) && element.classList.contains(cls)) {
          element.classList.remove(cls);
        }
      }
      // Add new classes
      for (const cls of nextClasses) {
        if (!element.classList.contains(cls)) {
          element.classList.add(cls);
        }
      }
      applied.set('class', nextClasses);
    } else if (name === 'style') {
      const prevProps: string[] = applied.get('style') ?? [];
      const nextStyle = parseStyles(value);
      const nextProps = Object.keys(nextStyle);

      // Remove properties we previously added but are no longer present
      for (const prop of prevProps) {
        if (!nextProps.includes(prop)) {
          element.style.removeProperty(prop);
        }
      }
      // Set/update properties
      for (const [prop, raw] of Object.entries(nextStyle)) {
        const { value: val, priority } = parseStyleValue(raw);
        element.style.setProperty(prop, val, priority);
      }
      applied.set('style', nextProps);
    } else {
      setAttr(element, name, value);
      applied.set(name, value);
    }
  }
}
